import React, { useCallback, useMemo, useState, useEffect } from "react";
import { Button, Modal } from "antd";
import { useTranslation } from "react-i18next";
import Table, { ColumnsType } from "antd/lib/table";
import { parseISO } from "date-fns";
import { HistoryOutlined } from "@ant-design/icons";
import { Ihistory, IUser } from "../index";
import api from "../../../services/api";
import { useAuth } from "../../../hooks/auth";
import UserRoles from "../../../enums/UserRoles";
import UserAvatar from "../../../components/UserAvatar";

interface IFieldValueHistoryModalProps {
  userUpdate: IUser;
  fieldName: string;
  title: string;
}

const FieldValueHistoryModal: React.FC<IFieldValueHistoryModalProps> = ({
  userUpdate,
  fieldName,
  title,
}) => {
  const { t } = useTranslation(["consultants"]);
  const [visible, setVisible] = useState<boolean>(false);
  const [history, setHistory] = useState<Ihistory[]>();
  const [modificatoryAvatar, setModificatoryAvatar] = useState<{
    name: string;
    avatar: string | null;
  }>({ name: "", avatar: "" });
  const [modificatoryId, setModificatoryId] = useState("");
  const { user } = useAuth();

  const isLoggedUserManager = useMemo(
    () =>
      user.role === UserRoles.Manager || user.role === UserRoles.Administrative,
    [user.role]
  );

  const handleFindHistory = useCallback(() => {
    api
      .get(`/consultant/${userUpdate.id}/${fieldName}`, {
        params: { field_name: fieldName },
      })
      .then((response) => {
        setHistory(response.data);
      });
  }, [userUpdate.id, fieldName]);

  const handleClose = useCallback(() => {
    setVisible(false);
  }, []);

  useEffect(() => {
    if (modificatoryId) {
      api.get<IUser>(`/users/${modificatoryId}`).then((response) => {
        setModificatoryAvatar({
          avatar: response.data.avatar,
          name: response.data.name,
        });
      });
    }
  }, [modificatoryId, visible]);

  const actionsHistoryColumns: ColumnsType<Ihistory> = [
    {
      title: t("history.date"),
      dataIndex: "create_at",
      render: (date) =>
        date &&
        `${parseISO(date).toLocaleDateString("pt-BR")} às ${parseISO(
          date
        ).toLocaleTimeString("pt-BR")}`,
    },
    {
      title: t("history.modificador"),
      dataIndex: "modificatory_id",
      render: (modificatory_id) => {
        setModificatoryId(modificatory_id);
        return (
          (
            <UserAvatar
              name={modificatoryAvatar.name}
              url={modificatoryAvatar.avatar || ""}
              size={36}
            />
          ) || ""
        );
      },
    },
    {
      title,
      dataIndex: fieldName,
    },
  ];

  return isLoggedUserManager ? (
    <>
      <Modal
        title={`${title}`}
        visible={visible}
        destroyOnClose
        width={800}
        onCancel={() => handleClose()}
        getContainer={false}
        footer={[
          <Button type="primary" key="ok" onClick={() => handleClose()}>
            Ok
          </Button>,
        ]}
      >
        <Table
          dataSource={history || []}
          rowKey={(record) => record.id}
          columns={actionsHistoryColumns}
          pagination={false}
          size="small"
        />
      </Modal>
      <Button
        type="link"
        shape="circle"
        icon={<HistoryOutlined />}
        onClick={() => {
          handleFindHistory();
          setVisible((oldVisible) => !oldVisible);
        }}
      />
    </>
  ) : (
    <></>
  );
};

export default FieldValueHistoryModal;
